// Helper SEO/GEO per-route. Ogni route module (app/routes/*) esporta `meta` =
// buildMeta(...), che emette il SET COMPLETO di descriptor (title, description,
// canonical, Open Graph, Twitter). In React Router framework mode il `meta` della
// route foglia SOSTITUISCE quello del root, quindi ogni route deve essere completa.
// Vedi ADR 0004.
import type { MetaDescriptor } from 'react-router'

// URL assoluto del sito, per canonical e og:url (i crawler IA preferiscono URL
// assoluti). Override via VITE_SITE_URL in CI; default = dominio di produzione.
const SITE_URL = (import.meta.env.VITE_SITE_URL ?? 'https://www.spadhausen.com').replace(/\/+$/, '')
const SITE_NAME = 'Spadhausen'
const DEFAULT_OG = '/assets/og-cover.jpg'
// Dimensioni reali di og-cover.jpg (1200×630): dichiararle aiuta alcuni scraper (LinkedIn)
// a renderizzare subito l'anteprima senza riscaricare l'immagine. Alt = descrizione brand.
const DEFAULT_OG_W = '1200'
const DEFAULT_OG_H = '630'
const DEFAULT_OG_ALT = 'Spadhausen — internet in fibra a Ravenna e in Romagna'
// In staging tutto il sito va in noindex (impostato in CI). In produzione resta
// indicizzabile e solo le pagine marcate noindex (es. articoli placeholder) lo sono.
const FORCE_NOINDEX = import.meta.env.VITE_NOINDEX === 'true'

export interface SeoInput {
  title: string
  description: string
  canonical: string // path assoluto dal root, es. '/offerte-casa' o '/'
  ogImage?: string
  ogType?: 'website' | 'article'
  noindex?: boolean
}

export function absoluteUrl(path: string): string {
  return SITE_URL + (path.startsWith('/') ? path : `/${path}`)
}

export function buildMeta(input: SeoInput): MetaDescriptor[] {
  const url = absoluteUrl(input.canonical)
  const image = absoluteUrl(input.ogImage ?? DEFAULT_OG)
  const ogType = input.ogType ?? 'website'

  // width/height/alt solo per l'immagine di default (og-cover.jpg 1200×630): se una pagina
  // passa una ogImage di formato diverso le ometto, per non dichiarare misure errate.
  const usingDefaultImage = !input.ogImage
  const ogImageExtra: MetaDescriptor[] = usingDefaultImage
    ? [
        { property: 'og:image:width', content: DEFAULT_OG_W },
        { property: 'og:image:height', content: DEFAULT_OG_H },
        { property: 'og:image:alt', content: DEFAULT_OG_ALT },
      ]
    : []
  const twitterImageExtra: MetaDescriptor[] = usingDefaultImage
    ? [{ name: 'twitter:image:alt', content: DEFAULT_OG_ALT }]
    : []

  const out: MetaDescriptor[] = [
    { title: input.title },
    { name: 'description', content: input.description },
    { tagName: 'link', rel: 'canonical', href: url },
    { property: 'og:type', content: ogType },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:locale', content: 'it_IT' },
    { property: 'og:title', content: input.title },
    { property: 'og:description', content: input.description },
    { property: 'og:url', content: url },
    { property: 'og:image', content: image },
    ...ogImageExtra,
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: input.title },
    { name: 'twitter:description', content: input.description },
    { name: 'twitter:image', content: image },
    ...twitterImageExtra,
  ]

  if (FORCE_NOINDEX) out.push({ name: 'robots', content: 'noindex, nofollow' })
  else if (input.noindex) out.push({ name: 'robots', content: 'noindex, follow' })
  return out
}
